/**
 * BugReportUI - Modal UI for submitting bug reports
 *
 * Creates a Cassette Future styled modal with:
 * - Textarea for bug description
 * - Submit/Cancel buttons
 * - Loading state during submission
 * - Success/error feedback
 */

import { BugReportManager } from '../services/BugReportManager.js';

/**
 * Create a CF-styled modal for bug reporting
 */
function createBugReportModal({ onClose }) {
    const overlay = document.createElement('div');
    overlay.className = 'cf-ui cf-modal-overlay';

    const modal = document.createElement('div');
    modal.className = 'cf-modal cf-modal-bug-report';
    modal.style.maxWidth = '400px';

    // Header
    const header = document.createElement('div');
    header.className = 'cf-modal-header';

    const titleEl = document.createElement('h2');
    titleEl.className = 'cf-modal-title';
    titleEl.textContent = 'REPORT BUG';
    header.appendChild(titleEl);

    const closeBtn = document.createElement('button');
    closeBtn.className = 'cf-modal-close';
    closeBtn.textContent = '\u00D7';
    header.appendChild(closeBtn);

    modal.appendChild(header);

    // Body
    const body = document.createElement('div');
    body.className = 'cf-modal-body';
    modal.appendChild(body);

    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    const close = () => {
        if (onClose) onClose();
        overlay.remove();
    };

    closeBtn.addEventListener('click', close);
    overlay.addEventListener('click', (e) => {
        if (e.target === overlay) close();
    });

    // Escape key
    const handleEscape = (e) => {
        if (e.key === 'Escape') {
            close();
            document.removeEventListener('keydown', handleEscape);
        }
    };
    document.addEventListener('keydown', handleEscape);

    return { overlay, modal, body, close };
}

/**
 * Show the bug report modal
 * @param {Object} options
 * @param {Object} options.gameState - GameState instance
 * @param {Object} options.accountManager - AccountManager instance (optional)
 * @returns {Promise<boolean>} true if report was submitted successfully
 */
export function showBugReportModal({ gameState, accountManager }) {
    return new Promise((resolve) => {
        const modal = createBugReportModal({
            onClose: () => resolve(false)
        });

        const content = modal.body;

        // Use form element for iOS compatibility
        const form = document.createElement('form');
        form.setAttribute('action', 'javascript:void(0)');

        // Instructions
        const instructions = document.createElement('p');
        instructions.style.cssText = 'margin: 0 0 var(--cf-space-md) 0; font-size: var(--cf-font-size-sm); color: var(--cf-text-secondary);';
        instructions.textContent = 'Describe the issue you encountered. Your game data and debug logs will be attached automatically.';
        form.appendChild(instructions);

        // Text input (contenteditable div for iOS Firefox compatibility)
        const textarea = document.createElement('div');
        textarea.className = 'cf-input';
        textarea.setAttribute('contenteditable', 'true');
        textarea.setAttribute('data-placeholder', 'What happened? What did you expect to happen?');
        textarea.style.cssText = `
            width: 100%;
            min-height: 120px;
            font-family: inherit;
            font-size: var(--cf-font-size-sm);
            margin-bottom: var(--cf-space-md);
            background: var(--cf-bg-input, rgba(0,0,0,0.3));
            border: 1px solid var(--cf-divider, rgba(255,255,255,0.1));
            color: var(--cf-text-primary, #fff);
            padding: var(--cf-space-sm);
            border-radius: 4px;
            overflow-y: auto;
            white-space: pre-wrap;
            word-wrap: break-word;
        `;
        form.appendChild(textarea);

        // Placeholder handling for contenteditable
        const updatePlaceholder = () => {
            if (!textarea.textContent.trim()) {
                textarea.setAttribute('data-empty', 'true');
            } else {
                textarea.removeAttribute('data-empty');
            }
        };
        textarea.addEventListener('input', updatePlaceholder);
        textarea.addEventListener('blur', updatePlaceholder);
        textarea.addEventListener('focus', updatePlaceholder);
        updatePlaceholder();

        // Add placeholder style
        const placeholderStyle = document.createElement('style');
        placeholderStyle.textContent = `
            [contenteditable][data-empty="true"]:before {
                content: attr(data-placeholder);
                color: var(--cf-text-disabled, #666);
                pointer-events: none;
            }
        `;
        document.head.appendChild(placeholderStyle);

        // Status message (hidden initially)
        const statusMsg = document.createElement('div');
        statusMsg.style.cssText = 'margin-bottom: var(--cf-space-md); font-size: var(--cf-font-size-sm); display: none;';
        form.appendChild(statusMsg);

        // Buttons
        const buttonGroup = document.createElement('div');
        buttonGroup.className = 'cf-button-group';
        buttonGroup.style.cssText = 'display: flex; gap: var(--cf-space-sm); justify-content: flex-end;';

        const cancelBtn = document.createElement('button');
        cancelBtn.className = 'cf-button';
        cancelBtn.setAttribute('type', 'button');
        cancelBtn.textContent = 'CANCEL';

        const submitBtn = document.createElement('button');
        submitBtn.className = 'cf-button primary';
        submitBtn.setAttribute('type', 'button');
        submitBtn.textContent = 'SUBMIT';

        buttonGroup.appendChild(cancelBtn);
        buttonGroup.appendChild(submitBtn);
        form.appendChild(buttonGroup);

        content.appendChild(form);

        // Don't auto-focus on iOS - causes keyboard/interaction issues
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        if (!isIOS) {
            setTimeout(() => textarea.focus(), 100);
        }

        // Cancel button
        cancelBtn.addEventListener('click', () => {
            modal.close();
            resolve(false);
        });

        // Submit button
        submitBtn.addEventListener('click', async () => {
            const description = (textarea.value || textarea.textContent || '').trim();

            if (!description) {
                statusMsg.textContent = 'Please describe the issue.';
                statusMsg.style.color = 'var(--cf-warning, #f5a623)';
                statusMsg.style.display = 'block';
                textarea.focus();
                return;
            }

            // Disable inputs
            textarea.contentEditable = 'false';
            submitBtn.disabled = true;
            cancelBtn.disabled = true;
            submitBtn.textContent = 'SENDING...';
            statusMsg.style.display = 'none';

            try {
                const bugReportManager = new BugReportManager(gameState, accountManager);
                const result = await bugReportManager.submit(description);

                if (result.success) {
                    statusMsg.textContent = 'Report sent! Thank you for your feedback.';
                    statusMsg.style.color = 'var(--cf-positive, #5fd47a)';
                    statusMsg.style.display = 'block';
                    submitBtn.textContent = 'SENT';

                    // Close after a short delay
                    setTimeout(() => {
                        modal.close();
                        resolve(true);
                    }, 1500);
                } else {
                    throw new Error(result.error || 'Unknown error');
                }
            } catch (err) {
                console.error('[BugReport] Submit error:', err);
                statusMsg.textContent = 'Failed to send report. Please try again.';
                statusMsg.style.color = 'var(--cf-warning, #f5a623)';
                statusMsg.style.display = 'block';

                // Re-enable inputs
                textarea.contentEditable = 'true';
                submitBtn.disabled = false;
                cancelBtn.disabled = false;
                submitBtn.textContent = 'SUBMIT';
            }
        });
    });
}

/**
 * Set up bug report button event listeners
 * @param {Object} options
 * @param {Object} options.gameState - GameState instance
 * @param {Object} options.accountManager - AccountManager instance (optional)
 */
export function setupBugReportButtons({ gameState, accountManager }) {
    // Start screen bug button
    const startScreenBtn = document.getElementById('bug-report-button');
    if (startScreenBtn) {
        startScreenBtn.addEventListener('click', () => {
            showBugReportModal({ gameState, accountManager });
        });
    }
}
